// Creating the draft's submissions.
//
// Kicking extraction used to live here too — one request per student, each
// naming an attachment generation and retrying its own 409. That is one call to
// POST /assignments/{id}/submissions/extractText now, so none of the fencing it
// needed is the client's business any more. See utils/extractionRun.js.
//
// Replaces bulkDraft.js. The two steps it used to take — make sure an assignment
// exists, then create a staging draft — are gone: the assignment is created at the
// Question step now, and there is no staging resource. Pages go straight onto real
// submissions at "Save and continue".
import { createSubmissionBatch } from '../../../service';
import {
  buildSubmissionBatchPayload,
  isEmptySubmissionBatch,
  submissionBatchDropError,
  submissionBatchLimitError,
} from './submissionBatch';
import { submissionBatchCodeMessage } from './submissionBatchErrors';
import { rememberPendingBatch, clearPendingBatch, isFinalBatchRejection } from './pendingBatch';

// Checked BEFORE the empty-batch rule, which would otherwise report "assign a page
// to someone" for a teacher who assigned plenty and lost them all to failed uploads.
const localValidationError = (message) =>
  Object.assign(new Error(message), { code: 'LOCAL_BATCH_VALIDATION_FAILED' });

const rejectDropped = (dropped, students) => {
  const dropError = submissionBatchDropError(dropped, students);
  if (dropError) throw localValidationError(dropError);
};

const rejectUnsendable = (payload, students) => {
  if (isEmptySubmissionBatch(payload)) {
    throw localValidationError(submissionBatchCodeMessage('EMPTY_STUDENTS'));
  }
  // Removed shape validation - allow students to have both scanned pages and typed text
  // const shapeError = submissionBatchShapeError(payload, students);
  // if (shapeError) throw new Error(shapeError);
  const limitError = submissionBatchLimitError(payload);
  if (limitError) throw localValidationError(limitError);
};

// A same-key replay of a save that already went through. Its own words: the
// DRAFT_ALREADY_SUBMITTED code now means an earlier upload is still open.
const REPLAY_MESSAGE = 'These responses have already been added.';

const replayOutcome = (batch) => {
  const alreadySubmitted = Boolean(batch?.alreadySubmitted);
  // Round 4c m1: the last upload's routed redo hasn't been handed in, and the
  // server's copy names whose (06 §5.8).
  const message = batch?.details?.pendingRedoSubmissionIds?.length && batch?.message
    ? batch.message
    : REPLAY_MESSAGE;
  return {
    alreadySubmitted,
    alreadySubmittedMessage: alreadySubmitted ? message : null,
  };
};

/**
 * @param {Object} params
 * @param {string} params.assignmentId
 * @param {Array} params.pages
 * @param {Object} params.studentAssignments - Map of studentId -> pageIds
 * @param {Array} params.students
 * @param {string} params.idempotencyKey - so a double-click creates one set, not two
 * @returns {{submissionIds: string[], routedPageIdsByStudent: object,
 * alreadySubmitted: boolean, alreadySubmittedMessage: ?string}}
 * The routing is returned alongside the ids because the poll answers positionally
 * against it. `alreadySubmittedMessage` is non-null only on a replay, so a caller
 * cannot report one as a fresh commit by simply forgetting to check the flag.
 */
export const createSubmissionsFor = async ({
  assignmentId,
  pages,
  studentAssignments,
  students,
  idempotencyKey,
  replacesSubmissionIds,
}) => {
  const { payload, routedPageIdsByStudent, dropped } =
    buildSubmissionBatchPayload(pages, studentAssignments, students);
  rejectDropped(dropped, students);
  rejectUnsendable(payload, students);

  rememberPendingBatch(assignmentId, idempotencyKey);
  let batch;
  try {
    batch = await createSubmissionBatch(assignmentId, {
      ...payload,
      ...(replacesSubmissionIds?.length ? { replacesSubmissionIds } : {}),
    }, idempotencyKey);
    clearPendingBatch(assignmentId, idempotencyKey);
  } catch (error) {
    if (isFinalBatchRejection(error)) clearPendingBatch(assignmentId, idempotencyKey);
    throw error;
  }
  const submissionIds = batch?.submissionIds || [];
  if (!submissionIds.length) throw new Error('Failed to create submissions');
  return {
    submissionIds,
    routedPageIdsByStudent,
    ...replayOutcome(batch),
  };
};
